<template>
  <div class="deposit">
    <button class="btn-back" @click="router.push({ name: 'player.wallet' })">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="m15 18-6-6 6-6"/>
      </svg>
      {{ $t('player.deposit.back') }}
    </button>

    <h2 class="page-title">{{ $t('player.deposit.title') }}</h2>

    <!-- Balance Summary -->
    <div v-if="walletStore.balance" class="balance-summary">
      <span class="balance-label">{{ $t('player.deposit.current_balance') }}:</span>
      <span class="balance-value">{{ formatAmount(walletStore.balance.balance) }}</span>
      <span class="balance-currency">{{ walletStore.balance.currency }}</span>
    </div>

    <!-- Deposit Form Card -->
    <div class="deposit-card">
      <!-- Payment Method Tabs -->
      <div class="form-group">
        <label class="form-label">{{ $t('player.deposit.payment_method') }}</label>
        <div class="method-tabs">
          <button
            v-for="method in paymentMethods"
            :key="method.value"
            class="method-tab"
            :class="{ active: selectedMethod === method.value }"
            @click="selectMethod(method.value)"
          >
            <span class="method-icon" v-html="sanitizeSvg(method.icon)"></span>
            <span class="method-name">{{ method.label }}</span>
          </button>
        </div>
      </div>

      <!-- Currency -->
      <div class="form-group">
        <label class="form-label" for="currency">{{ $t('player.deposit.currency_label') }}</label>
        <select id="currency" v-model="currency" class="form-select">
          <option value="USD">USD - US Dollar</option>
          <option value="EUR">EUR - Euro</option>
          <option value="GBP">GBP - British Pound</option>
          <option value="BTC">BTC - Bitcoin</option>
          <option value="ETH">ETH - Ethereum</option>
          <option value="USDT">USDT - Tether</option>
        </select>
      </div>

      <!-- Amount -->
      <div class="form-group">
        <label class="form-label" for="amount">{{ $t('player.deposit.amount_label') }}</label>
        <div class="amount-input-wrap">
          <span class="amount-prefix">{{ currencySymbol }}</span>
          <input
            id="amount"
            v-model="amount"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            class="form-input amount-input"
            :disabled="depositSubmitted"
          >
        </div>
        <div class="quick-amounts">
          <button
            v-for="qa in quickAmounts"
            :key="qa"
            class="quick-amount-btn"
            :disabled="depositSubmitted"
            @click="amount = String(qa)"
          >
            {{ currencySymbol }}{{ qa }}
          </button>
        </div>
      </div>

      <!-- Deposit Limits Info -->
      <div v-if="walletStore.depositLimits && walletStore.depositLimits.usage.length > 0" class="limits-info">
        <div class="limits-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </div>
        <div class="limits-text">
          <p class="limits-title">{{ $t('player.deposit.limits_title') }}</p>
          <p v-for="usage in walletStore.depositLimits.usage" :key="usage.period" class="limits-detail">
            {{ capitalize(usage.period) }}: {{ formatAmount(usage.deposited) }} / {{ formatAmount(usage.limit) }}
            {{ $t('player.deposit.limits_remaining', { remaining: formatAmount(usage.remaining) }) }}
          </p>
        </div>
      </div>

      <!-- Jurisdiction: payment method restriction notice -->
      <JurisdictionBlockNotice
        v-if="paymentMethodRestricted"
        type="payment"
        :message="paymentRestrictionMessage"
        class="jurisdiction-notice"
      />

      <!-- Error Messages -->
      <div v-if="walletStore.depositError || formError" class="error-banner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <span>{{ walletStore.depositError || formError }}</span>
      </div>

      <!-- Deposit Result: Manual -->
      <div v-if="depositSubmitted && selectedMethod === 'manual'" class="deposit-result">
        <div class="result-icon result-icon-manual">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
        </div>
        <h4 class="result-title">{{ $t('player.deposit.bank_transfer_title') }}</h4>
        <p class="result-desc">{{ $t('player.deposit.bank_transfer_desc') }}</p>
        <div v-if="walletStore.depositResponse" class="result-details">
          <div v-for="(value, key) in walletStore.depositResponse.instructions" :key="key" class="result-detail-row">
            <span class="result-detail-label">{{ formatInstructionKey(String(key)) }}</span>
            <span class="result-detail-value">{{ value }}</span>
          </div>
        </div>
        <div class="result-status">
          <span class="status-badge pending">{{ $t('player.deposit.status_pending') }}</span>
          <span class="result-status-text">{{ $t('player.deposit.awaiting_admin') }}</span>
        </div>
      </div>

      <!-- Deposit Result: Crypto -->
      <div v-if="depositSubmitted && selectedMethod === 'crypto'" class="deposit-result">
        <div class="result-icon result-icon-crypto">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <h4 class="result-title">{{ $t('player.deposit.crypto_title') }}</h4>
        <p class="result-desc">{{ $t('player.deposit.crypto_desc') }}</p>

        <!-- QR Code -->
        <div v-if="qrCodeDataUrl" class="qr-container">
          <img :src="qrCodeDataUrl" :alt="$t('player.deposit.qr_alt')" class="qr-image" />
        </div>

        <!-- Deposit Details -->
        <div v-if="walletStore.depositResponse" class="result-details">
          <div v-for="(value, key) in walletStore.depositResponse.instructions" :key="key" class="result-detail-row">
            <span class="result-detail-label">{{ formatInstructionKey(String(key)) }}</span>
            <span class="result-detail-value copy-value" @click="copyToClipboard(String(value))">
              {{ value }}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </span>
          </div>
        </div>

        <!-- Polling Status -->
        <div class="result-status">
          <span v-if="walletStore.depositResponse?.status === 'pending'" class="status-badge pending">
            <span class="polling-dot"></span>
            {{ $t('player.deposit.waiting_confirmation') }}
          </span>
          <span v-else-if="walletStore.depositResponse?.status === 'completed'" class="status-badge completed">
            {{ $t('player.deposit.confirmed') }}
          </span>
          <span v-else class="status-badge pending">{{ walletStore.depositResponse?.status }}</span>
        </div>
      </div>

      <!-- Deposit Result: Card (Stripe) -->
      <div v-if="depositSubmitted && selectedMethod === 'card'" class="deposit-result">
        <div class="result-icon result-icon-card">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
        </div>
        <h4 class="result-title">{{ $t('player.deposit.card_title') }}</h4>
        <p class="result-desc">{{ $t('player.deposit.card_desc') }}</p>

        <StripeDepositForm
          v-if="stripeClientSecret && stripePublishableKey"
          :client-secret="stripeClientSecret"
          :publishable-key="stripePublishableKey"
          :amount="stripeAmountCents"
          :currency="currency"
          @success="onStripeSuccess"
          @error="onStripeError"
        />
      </div>

      <!-- Submit Button -->
      <button
        v-if="!depositSubmitted"
        class="btn-deposit"
        :disabled="!canSubmit"
        @click="submitDeposit"
      >
        <svg v-if="!walletStore.loadingDeposit" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        <span v-if="walletStore.loadingDeposit" class="spinner"></span>
        {{ walletStore.loadingDeposit ? $t('common.processing') : $t('wallet.deposit') }}
      </button>

      <!-- New Deposit Button (shown after a deposit was submitted) -->
      <button
        v-if="depositSubmitted"
        class="btn-new-deposit"
        @click="resetForm"
      >
        {{ $t('player.deposit.another') }}
      </button>
    </div>

    <!-- Recent Deposits -->
    <div class="history-section">
      <h3 class="section-subtitle">{{ $t('player.deposit.recent') }}</h3>
      <div v-if="recentDeposits.length > 0" class="history-list">
        <div v-for="tx in recentDeposits" :key="tx.id" class="history-item">
          <div class="history-item-left">
            <span class="history-type">{{ $t('player.deposit.type_label') }}</span>
            <span class="history-date">{{ formatDate(tx.created_at) }}</span>
          </div>
          <div class="history-item-right">
            <span class="history-amount">+{{ formatAmount(tx.amount) }}</span>
            <span class="status-badge" :class="tx.status">{{ tx.status }}</span>
          </div>
        </div>
      </div>
      <div v-else class="history-empty">
        <p>{{ $t('player.deposit.no_history') }}</p>
        <p class="history-empty-sub">{{ $t('player.deposit.no_history_sub') }}</p>
      </div>
    </div>

    <!-- Provably Fair Link -->
    <div class="info-section">
      <div class="info-card">
        <div class="info-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </div>
        <div>
          <p class="info-title">{{ $t('player.deposit.provably_fair_title') }}</p>
          <p class="info-desc">{{ $t('player.deposit.provably_fair_desc') }}</p>
        </div>
        <button class="btn-link" @click="router.push({ name: 'provably-fair.verify' })">
          {{ $t('common.learn_more') }}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'
import StripeDepositForm from '@/components/payment/StripeDepositForm.vue'
import JurisdictionBlockNotice from '@/components/JurisdictionBlockNotice.vue'
import { useJurisdiction } from '@/composables/useJurisdiction'
import { sanitizeSvg } from '@/composables/useSanitize'
import QRCode from 'qrcode'

const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const toast = useToastStore()
const { jurisdiction, fetchJurisdiction } = useJurisdiction()

// Show a notice when jurisdiction restricts available payment methods
const paymentMethodRestricted = computed(() => {
  return jurisdiction.value?.allowed_payment_methods !== null &&
    (jurisdiction.value?.allowed_payment_methods?.length ?? 0) > 0
})

const paymentRestrictionMessage = computed(() => {
  const allowed = jurisdiction.value?.allowed_payment_methods
  if (!allowed || allowed.length === 0) return undefined
  const list = allowed.join(', ')
  return t('player.deposit.payment_restriction', { list })
})

const currency = ref('USD')
const amount = ref('')
const selectedMethod = ref('manual')
const depositSubmitted = ref(false)
const formError = ref<string | null>(null)
const qrCodeDataUrl = ref<string | null>(null)
const stripeClientSecret = ref<string | null>(null)
const stripePublishableKey = ref<string | null>(null)
const stripeAmountCents = ref(0)

const quickAmounts = [10, 25, 50, 100, 500]

const paymentMethods = computed(() => [
  {
    value: 'manual',
    label: t('player.deposit.method_bank_transfer'),
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>',
  },
  {
    value: 'card',
    label: t('player.deposit.method_credit_card'),
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/><line x1="7" y1="15" x2="13" y2="15"/></svg>',
  },
  {
    value: 'crypto',
    label: t('player.deposit.method_cryptocurrency'),
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
  },
])

const currencySymbol = computed(() => {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '\u20AC',
    GBP: '\u00A3',
    BTC: '\u20BF',
    ETH: '\u039E',
    USDT: '$',
  }
  return symbols[currency.value] || '$'
})

const canSubmit = computed(() => {
  const numAmount = parseFloat(amount.value)
  return numAmount > 0 && !walletStore.loadingDeposit
})

const recentDeposits = computed(() => {
  return walletStore.transactions
    .filter((tx) => tx.type === 'credit' && (tx.notes?.toLowerCase().includes('deposit') ?? false))
    .slice(0, 5)
})

// Watch for deposit status change from polling
watch(
  () => walletStore.depositResponse?.status,
  (newStatus, oldStatus) => {
    if (oldStatus === 'pending' && newStatus === 'completed') {
      toast.success(t('player.deposit.toast_confirmed'))
      fetchRecentDeposits()
    }
  },
)

function formatAmount(val: number): string {
  return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function formatInstructionKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function selectMethod(method: string) {
  if (!depositSubmitted.value) {
    selectedMethod.value = method
  }
}

async function generateQrCode(data: string) {
  try {
    qrCodeDataUrl.value = await QRCode.toDataURL(data, {
      width: 200,
      margin: 2,
      color: {
        dark: '#ffffff',
        light: '#213743',
      },
    })
  } catch {
    qrCodeDataUrl.value = null
  }
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(t('common.copied'))
  } catch {
    toast.error(t('common.copy_failed'))
  }
}

async function submitDeposit() {
  formError.value = null

  const numAmount = parseFloat(amount.value)
  if (!numAmount || numAmount <= 0) {
    formError.value = t('player.deposit.invalid_amount')
    return
  }

  // Convert display amount to cents (integer)
  const amountCents = Math.round(numAmount * 100)

  try {
    const response = await walletStore.initiateDeposit(amountCents, currency.value, selectedMethod.value)
    depositSubmitted.value = true

    // Handle card (Stripe) deposits — extract client_secret and publishable_key
    if (selectedMethod.value === 'card' && response.instructions) {
      stripeClientSecret.value = response.instructions.client_secret as string ?? null
      stripePublishableKey.value = response.instructions.publishable_key as string ?? null
      stripeAmountCents.value = amountCents
    }

    // Generate QR for crypto deposits
    if (selectedMethod.value === 'crypto' && response.instructions.qr_data) {
      await generateQrCode(String(response.instructions.qr_data))
    }

    // Start polling for status updates (manual/crypto — card is handled by Stripe)
    if (response.status === 'pending' && selectedMethod.value !== 'card') {
      walletStore.pollDepositStatus(response.transaction_id)
    }

    toast.info(t('player.deposit.initiated'))
    fetchRecentDeposits()
  } catch {
    // Error is already set in walletStore.depositError
  }
}

function onStripeSuccess() {
  toast.success(t('player.deposit.stripe_success'))
  // Start polling to detect when the webhook confirms the deposit
  if (walletStore.depositResponse) {
    walletStore.pollDepositStatus(walletStore.depositResponse.transaction_id)
  }
  fetchRecentDeposits()
  walletStore.fetchBalance()
}

function onStripeError(message: string) {
  formError.value = message
}

function resetForm() {
  walletStore.stopDepositPolling()
  walletStore.depositResponse = null
  walletStore.depositError = null
  depositSubmitted.value = false
  amount.value = ''
  formError.value = null
  qrCodeDataUrl.value = null
  stripeClientSecret.value = null
  stripePublishableKey.value = null
  stripeAmountCents.value = 0
}

function fetchRecentDeposits() {
  // Fetch latest transactions to populate recent deposits
  walletStore.fetchTransactions(1)
}

onMounted(() => {
  if (!walletStore.balance) {
    walletStore.fetchBalance()
  }
  walletStore.fetchDepositLimits()
  fetchRecentDeposits()
  fetchJurisdiction()
})

onBeforeUnmount(() => {
  walletStore.stopDepositPolling()
})
</script>

<style scoped>
.deposit {
  padding: var(--space-6, 24px);
  max-width: 600px;
  margin: 0 auto;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Back Button */
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: color var(--transition-fast, 150ms ease);
  margin-bottom: 16px;
}

.btn-back:hover {
  color: var(--text-primary, #fff);
}

/* Page Title */
.page-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
}

/* Balance Summary */
.balance-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(0, 231, 1, 0.06);
  border: 1px solid rgba(0, 231, 1, 0.15);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
}

.balance-label {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.balance-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 15px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

.balance-currency {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
}

/* Deposit Card */
.deposit-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  margin-bottom: 24px;
}

/* Form */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 8px;
}

.form-select,
.form-input {
  width: 100%;
  padding: 11px 14px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  font-size: 14px;
  font-family: inherit;
  transition: border-color var(--transition-fast, 150ms ease);
  box-sizing: border-box;
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%237f8fa4' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  cursor: pointer;
}

.form-select option {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

/* Payment Method Tabs */
.method-tabs {
  display: flex;
  gap: 10px;
}

.method-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: var(--bg-primary, #0f1923);
  border: 2px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all var(--transition-fast, 150ms ease);
}

.method-tab:hover {
  border-color: rgba(255, 255, 255, 0.15);
  color: var(--text-primary, #fff);
}

.method-tab.active {
  border-color: var(--accent-primary, #00e701);
  color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.06);
}

.method-icon {
  display: flex;
  align-items: center;
}

/* Amount Input */
.amount-input-wrap {
  position: relative;
}

.amount-prefix {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
}

.amount-input {
  padding-left: 32px;
}

.amount-input::-webkit-inner-spin-button,
.amount-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.amount-input[type="number"] {
  -moz-appearance: textfield;
}

/* Quick Amounts */
.quick-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.quick-amount-btn {
  padding: 6px 14px;
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-sm, 4px);
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all var(--transition-fast, 150ms ease);
}

.quick-amount-btn:hover:not(:disabled) {
  border-color: var(--accent-primary, #00e701);
  color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.06);
}

.quick-amount-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Deposit Limits Info */
.limits-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(255, 193, 7, 0.06);
  border: 1px solid rgba(255, 193, 7, 0.15);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
}

.limits-icon {
  color: var(--color-warning, #ffc107);
  flex-shrink: 0;
  margin-top: 1px;
}

.limits-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.limits-detail {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.6;
}

/* Jurisdiction notice */
.jurisdiction-notice {
  margin-bottom: 20px;
}

/* Error Banner */
.error-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.2);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
  color: var(--color-danger, #ed4245);
  font-size: 13px;
  font-weight: 500;
}

/* Deposit Result */
.deposit-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 16px;
  margin-bottom: 20px;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md, 8px);
}

.result-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.result-icon-manual {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

.result-icon-crypto {
  background: rgba(247, 147, 26, 0.15);
  color: #f7931a;
}

.result-icon-card {
  background: rgba(99, 91, 255, 0.15);
  color: #635bff;
}

.result-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
}

.result-desc {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
  max-width: 400px;
  margin-bottom: 20px;
}

/* QR Code */
.qr-container {
  padding: 12px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
}

.qr-image {
  display: block;
  width: 200px;
  height: 200px;
  border-radius: 4px;
}

/* Result Details */
.result-details {
  width: 100%;
  max-width: 400px;
  margin-bottom: 16px;
}

.result-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  gap: 12px;
}

.result-detail-row:last-child {
  border-bottom: none;
}

.result-detail-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-align: left;
  flex-shrink: 0;
}

.result-detail-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 12px;
  color: var(--text-primary, #fff);
  word-break: break-all;
  text-align: right;
}

.copy-value {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color var(--transition-fast, 150ms ease);
}

.copy-value:hover {
  color: var(--accent-primary, #00e701);
}

/* Result Status */
.result-status {
  display: flex;
  align-items: center;
  gap: 10px;
}

.result-status-text {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: var(--radius-md, 8px);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.status-badge.completed {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.status-badge.failed,
.status-badge.cancelled {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Polling Dot Animation */
.polling-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-warning, #ffa726);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* Deposit Button */
.btn-deposit {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 24px;
  font-size: 15px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  font-family: inherit;
  background: var(--accent-primary, #00e701);
  color: #000;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-deposit:hover:not(:disabled) {
  background: #00cc01;
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-deposit:disabled {
  background: var(--bg-tertiary, #213743);
  color: var(--text-disabled, #4a5568);
  cursor: not-allowed;
  opacity: 0.6;
}

/* New Deposit Button */
.btn-new-deposit {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  cursor: pointer;
  font-family: inherit;
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  transition: all var(--transition-fast, 150ms ease);
}

.btn-new-deposit:hover {
  border-color: var(--accent-primary, #00e701);
  color: var(--accent-primary, #00e701);
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* History Section */
.history-section {
  margin-bottom: 24px;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
}

.history-list {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background var(--transition-fast, 150ms ease);
}

.history-item:last-child {
  border-bottom: none;
}

.history-item:hover {
  background: rgba(0, 231, 1, 0.02);
}

.history-item-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.history-type {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.history-date {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.history-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.history-amount {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--color-success, #00e701);
}

.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  gap: 6px;
}

.history-empty-sub {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Info Section */
.info-section {
  margin-bottom: 24px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: rgba(20, 117, 225, 0.06);
  border: 1px solid rgba(20, 117, 225, 0.15);
  border-radius: var(--radius-md, 8px);
}

.info-icon {
  color: var(--accent-secondary, #1475e1);
  flex-shrink: 0;
}

.info-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 2px;
}

.info-desc {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
}

.btn-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  flex-shrink: 0;
  padding: 6px 12px;
  background: none;
  border: 1px solid rgba(20, 117, 225, 0.3);
  border-radius: var(--radius-sm, 4px);
  color: var(--accent-secondary, #1475e1);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-link:hover {
  background: rgba(20, 117, 225, 0.1);
  border-color: rgba(20, 117, 225, 0.5);
}

/* Responsive */
@media (max-width: 768px) {
  .deposit {
    padding: var(--space-4, 16px);
  }

  .method-tabs {
    flex-direction: column;
  }

  .info-card {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }

  .btn-link {
    margin-left: 0;
  }

  .quick-amounts {
    justify-content: center;
  }

  .result-detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .result-detail-value {
    text-align: left;
  }
}
</style>
